import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Field, IconButton, Switch } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { money, parseMoney } from '../../lib/format.ts';

/**
 * Variants of a product (a T-shirt in S, M, L and in red or blue): the attributes and their
 * values, and the list of variants with their own price, code, barcode and whether they are sold.
 * Each combination becomes a product of its own on the server (products.save_variants).
 */

export interface DraftValue {
  name: string;
  /** Typed extra price for this value; empty is none. */
  extra: string;
}
export interface DraftAttribute {
  key: string;
  name: string;
  values: DraftValue[];
}
export interface SavedAttribute {
  name: string;
  values: { name: string; price_delta: number }[];
}
export interface VariantRow {
  id: string;
  name: string;
  price: number;
  sku: string | null;
  barcode: string | null;
  active: boolean;
  variant_values?: Record<string, string>;
}
export interface VariantEdit {
  price: string;
  sku: string;
  barcode: string;
  active: boolean;
}

export const MAX_ATTRIBUTES = 3;
export const MAX_VARIANTS = 300;

const priceText = (minor: number) => (minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2));

export const draftAttributes = (saved: readonly SavedAttribute[] | undefined): DraftAttribute[] =>
  (saved ?? []).map((a, i) => ({ key: `a${i}`, name: a.name, values: a.values.map((v) => ({ name: v.name, extra: v.price_delta ? priceText(v.price_delta) : '' })) }));

export const savedAttributes = (draft: readonly DraftAttribute[]): SavedAttribute[] =>
  draft.map((a) => ({ name: a.name.trim(), values: a.values.map((v) => ({ name: v.name.trim(), price_delta: v.extra.trim() ? (parseMoney(v.extra) ?? 0) : 0 })) }));

/** How many variants the attributes make. */
export const variantCount = (draft: readonly DraftAttribute[]) => (draft.length ? draft.reduce((n, a) => n * Math.max(1, a.values.length), 1) : 0);

/** What is wrong with the attributes as typed, or null. */
export function attributesError(draft: readonly DraftAttribute[]): string | null {
  const names = new Set<string>();
  for (const a of draft) {
    if (!a.name.trim()) return t('variants.need_name');
    if (names.has(a.name.trim().toLowerCase())) return t('variants.twice', { name: a.name.trim() });
    names.add(a.name.trim().toLowerCase());
    if (!a.values.length) return t('variants.need_values', { name: a.name.trim() });
    if (a.values.some((v) => v.extra.trim() && parseMoney(v.extra.replace(/^-/, '')) === null)) return t('menu.price_invalid');
  }
  if (variantCount(draft) > MAX_VARIANTS) return t('variants.too_many', { n: variantCount(draft), max: MAX_VARIANTS });
  return null;
}

/** The attributes (Size, Colour, …) and their values, typed as chips. */
export function VariantsEditor({ attributes, onChange, error, basePrice }: { attributes: DraftAttribute[]; onChange: (a: DraftAttribute[]) => void; error: string | null; basePrice: number | null }) {
  const [typing, setTyping] = useState<Record<string, string>>({});
  const set = (key: string, change: Partial<DraftAttribute>) => onChange(attributes.map((a) => (a.key === key ? { ...a, ...change } : a)));
  const addValues = (a: DraftAttribute) => {
    const raw = (typing[a.key] ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!raw.length) return;
    const have = new Set(a.values.map((v) => v.name.toLowerCase()));
    const fresh = raw.filter((v, i) => !have.has(v.toLowerCase()) && raw.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i).map((name) => ({ name: name.slice(0, 40), extra: '' }));
    set(a.key, { values: [...a.values, ...fresh].slice(0, 30) });
    setTyping((s) => ({ ...s, [a.key]: '' }));
  };
  const n = variantCount(attributes);
  return (
    <fieldset className="option-group combo-editor variants-editor">
      <legend>
        {t('variants.title')} <span className="muted">{t('variants.hint')}</span>
      </legend>
      {attributes.map((a) => (
        <div key={a.key} className="combo-group-card">
          <div className="variants-attr-head">
            <Field label={t('variants.attribute')}>
              <input className="input" value={a.name} maxLength={40} placeholder={t('variants.attribute_placeholder')} onChange={(e) => set(a.key, { name: e.target.value })} />
            </Field>
            <IconButton icon="trash" label={t('variants.remove_attribute', { name: a.name || '…' })} onClick={() => onChange(attributes.filter((x) => x.key !== a.key))} />
          </div>
          {a.values.length ? (
            <ul className="variants-values" aria-label={t('variants.values_of', { name: a.name || '…' })}>
              {a.values.map((v, vi) => (
                <li key={v.name} className="variants-value">
                  <span className="variants-value-name">{v.name}</span>
                  <label className="combo-extra">
                    <span className="muted">+</span>
                    <input
                      className="input"
                      inputMode="decimal"
                      placeholder="0"
                      value={v.extra}
                      aria-label={t('variants.extra_for', { name: v.name })}
                      onChange={(e) => set(a.key, { values: a.values.map((x, i) => (i === vi ? { ...x, extra: e.target.value } : x)) })}
                    />
                  </label>
                  <IconButton icon="x" label={t('variants.remove_value', { name: v.name })} onClick={() => set(a.key, { values: a.values.filter((_, i) => i !== vi) })} />
                </li>
              ))}
            </ul>
          ) : null}
          <div className="variants-add">
            <input
              className="input"
              value={typing[a.key] ?? ''}
              maxLength={200}
              placeholder={t('variants.value_placeholder')}
              aria-label={t('variants.add_value_to', { name: a.name || '…' })}
              onChange={(e) => setTyping((s) => ({ ...s, [a.key]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addValues(a);
                }
              }}
            />
            <Button variant="secondary" size="sm" icon="plus" onClick={() => addValues(a)} disabled={!(typing[a.key] ?? '').trim()}>
              {t('variants.add_value')}
            </Button>
          </div>
        </div>
      ))}
      <div className="variants-foot">
        <Button
          variant="secondary"
          icon="plus"
          onClick={() => onChange([...attributes, { key: `n${Date.now()}`, name: attributes.length ? '' : t('variants.size'), values: [] }])}
          disabled={attributes.length >= MAX_ATTRIBUTES}
        >
          {attributes.length ? t('variants.add_attribute') : t('variants.add_first')}
        </Button>
        {n ? (
          <span className="muted">
            {tn('variants.will_make', n)}
            {basePrice !== null ? ` · ${t('variants.from_price', { price: money(basePrice) })}` : ''}
          </span>
        ) : null}
      </div>
      {error ? (
        <p className="callout callout-warn" role="alert">
          <Icon name="alert" size={16} /> {error}
        </p>
      ) : null}
    </fieldset>
  );
}

export const editOf = (v: VariantRow): VariantEdit => ({ price: priceText(v.price), sku: v.sku ?? '', barcode: v.barcode ?? '', active: v.active });

export const variantEditError = (edits: Record<string, VariantEdit>): string | null =>
  Object.values(edits).some((e) => parseMoney(e.price) === null) ? t('menu.price_invalid') : null;

/** The variants already made, each with its own price, code, barcode and on/off. */
export function VariantList({ variants, edits, onEdit }: { variants: VariantRow[]; edits: Record<string, VariantEdit>; onEdit: (id: string, e: VariantEdit) => void }) {
  if (!variants.length) return null;
  const sold = variants.filter((v) => (edits[v.id] ?? editOf(v)).active).length;
  return (
    <fieldset className="option-group">
      <legend>
        {t('variants.list')} <span className="muted">{t('variants.list_summary', { sold, all: variants.length })}</span>
      </legend>
      <div className="table-wrap">
        <table className="data-table variants-table">
          <thead>
            <tr>
              <th>{t('variants.variant')}</th>
              <th>{t('menu.price')}</th>
              <th className="left">{t('menu.sku')}</th>
              <th className="left">{t('menu.barcode')}</th>
              <th>{t('variants.on_sale')}</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => {
              const e = edits[v.id] ?? editOf(v);
              const label = Object.values(v.variant_values ?? {}).join(' · ') || v.name;
              return (
                <tr key={v.id} className={e.active ? '' : 'archived'}>
                  <td>{label}</td>
                  <td>
                    <input className="input variants-price" inputMode="decimal" value={e.price} aria-label={t('variants.price_of', { name: label })} onChange={(x) => onEdit(v.id, { ...e, price: x.target.value })} />
                  </td>
                  <td className="left">
                    <input className="input" value={e.sku} maxLength={40} aria-label={t('variants.sku_of', { name: label })} onChange={(x) => onEdit(v.id, { ...e, sku: x.target.value })} />
                  </td>
                  <td className="left">
                    <input className="input" value={e.barcode} maxLength={40} inputMode="numeric" aria-label={t('variants.barcode_of', { name: label })} onChange={(x) => onEdit(v.id, { ...e, barcode: x.target.value })} />
                  </td>
                  <td>
                    <Switch checked={e.active} label={t('variants.sell', { name: label })} onChange={(on) => onEdit(v.id, { ...e, active: on })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </fieldset>
  );
}
